import { peopleFor, peopleLabel } from './words';

export type TestStatus = 'draft' | 'public' | 'assigned' | 'archived';
export type TestBlock = 'products' | 'vehicles';
export type QuestionKind =
  | 'single_choice'
  | 'multiple_choice'
  | 'open_answer'
  | 'matching'
  | 'image_identification';
export type AssignmentState = 'available' | 'prepared' | 'paused' | 'completed' | 'expired';

export interface AssignmentCounts {
  total: number;
  available: number;
  prepared: number;
  paused: number;
  completed: number;
  expired: number;
}

export interface MatchedPerson {
  user_id: number;
  full_name: string;
}

/** Строка списка тестов: `GET /admin/test-overview`. */
export interface OverviewItem {
  id: number;
  title: string;
  description: string | null;
  block: TestBlock;
  subgroup: string | null;
  difficulty: string | null;
  passing_score?: number;
  time_limit_minutes: number;
  created_at: string | null;
  updated_at: string | null;
  questions_count: number;
  is_active: boolean;
  is_archived: boolean;
  status: TestStatus;
  issued_count: number;
  assignments: AssignmentCounts;
  matched_people: MatchedPerson[];
  matched_people_total: number;
}

export interface OverviewCounts {
  all: number;
  draft: number;
  public: number;
  assigned: number;
  archived: number;
}

export interface OverviewList {
  total: number;
  counts: OverviewCounts;
  items: OverviewItem[];
}

export interface OverviewQuestion {
  difficulty?: string | null;
  id: number;
  text: string;
  type: QuestionKind;
  order: number;
  image_url: string | null;
  answered_count: number;
  shared_in_tests: number;
  structure_ok: boolean;
  problems: string[];
}

/** Карточка теста: `GET /admin/test-overview/{id}`. */
export interface OverviewDetail extends OverviewItem {
  attempts: { total: number; completed: number };
  questions: OverviewQuestion[];
}

/** Строка «Готовых вопросов»: `GET /admin/questions/library`. */
export interface LibraryItem {
  question_id: number;
  text: string;
  type: QuestionKind;
  image_url: string | null;
  answers_count: number;
  tests_count: number;
  copies: number;
  source_test_id: number;
  source_test_title: string;
  block: TestBlock;
  subgroup: string | null;
  already_in_test: boolean;
  structure_ok: boolean;
  problems: string[];
  has_conflict: boolean;
}

export interface LibraryList {
  total: number;
  items: LibraryItem[];
}

export interface CopiedQuestion {
  position: number;
  source_question_id: number | null;
  question_id: number;
  order: number;
}

export interface SkippedQuestion {
  position: number;
  source_question_id: number | null;
  reason: 'duplicate_in_test' | 'duplicate_in_request' | 'invalid_structure';
  problems: string[];
}

export interface CopyResult {
  test_id: number;
  questions_count: number;
  copied: CopiedQuestion[];
  skipped: SkippedQuestion[];
}

export interface NewQuestionPayload {
  text: string;
  type: 'single_choice';
  image_url?: string;
  answers: Array<{ text: string; is_correct: boolean; order: number; image_url?: string }>;
}

export type ComposeItem = { source_question_id: number } | { new_question: NewQuestionPayload };

export interface ComposeRequest {
  title: string;
  description?: string | null;
  block: TestBlock;
  subgroup?: string | null;
  passing_score?: number;
  time_limit_minutes: number;
  items: ComposeItem[];
  publish: boolean;
}

export interface ComposeResult {
  test: OverviewItem;
  created: CopiedQuestion[];
  skipped: SkippedQuestion[];
}

/** Строка выдачи: `GET /admin/test-assignments`. */
export interface AssignmentRow {
  id: number;
  user_id: number;
  test_id: number;
  due_at: string | null;
  is_active: boolean;
  status: string;
  effective_state: AssignmentState;
  user_name?: string | null;
  latest_attempt_score?: number | null;
  latest_attempt_is_passed?: boolean | null;
  excluded_attempts_count?: number;
}

export interface Employee {
  id: number;
  full_name: string;
  position: string | null;
  department: string | null;
}

/** Один вопрос, который сотрудник ещё не встречал в сохранённых ответах. */
export interface UnansweredQuestion {
  question_id: number;
  text: string;
  type: QuestionKind;
  points: number;
  block: TestBlock | null;
  source_test_id: number | null;
  source_test_title: string | null;
  source_test_subgroup: string | null;
  already_assigned: boolean;
  active_assignment_titles: string[];
}

/** Ответ `GET /admin/users/{id}/unanswered-questions`. */
export interface UnansweredQuestions {
  user_id: number;
  user_name: string;
  questions: UnansweredQuestion[];
  total: number;
  available_to_assign: number;
  already_assigned: number;
}

export interface AssignUnansweredResult {
  message: string;
  user_id: number;
  user_name: string;
  tests_created: number;
  assignments_created: number;
  questions_assigned: number;
  created: Array<{
    test_id: number;
    test_title: string;
    assignment_id: number;
    block: TestBlock;
    questions_count: number;
  }>;
}

/** Слова выдач — `docs/DESIGN.md` §27, без синонимов. */
export const ASSIGNMENT_STATE_LABEL: Record<AssignmentState, string> = {
  available: 'Выдано',
  prepared: 'Подготовлено',
  paused: 'Приостановлено',
  completed: 'Завершено',
  expired: 'Просрочено',
};

export const FILTERS: Array<{ key: 'all' | TestStatus; label: string; help: string }> = [
  { key: 'all', label: 'Все', help: 'Показаны все тесты.' },
  { key: 'draft', label: 'Готовятся', help: 'Показаны тесты, которые пока никто не видит.' },
  { key: 'public', label: 'Доступны всем', help: 'Показаны тесты, которые видят все сотрудники.' },
  { key: 'assigned', label: 'Выданы людям', help: 'Показаны тесты, которые выданы конкретным людям.' },
  { key: 'archived', label: 'В архиве', help: 'Показаны тесты из архива. Сотрудники их не видят.' },
];

type StatusInput = Pick<OverviewItem, 'status' | 'issued_count' | 'assignments'>;

export function statusLabel(item: Pick<OverviewItem, 'status' | 'issued_count'>): string {
  switch (item.status) {
    case 'draft':
      return 'Готовится';
    case 'public':
      return 'Доступен всем';
    case 'assigned':
      return `Выдан: ${peopleLabel(item.issued_count)}`;
    default:
      return 'В архиве';
  }
}

export function statusSentence(item: StatusInput): string {
  switch (item.status) {
    case 'draft':
      return item.assignments.prepared > 0
        ? `Подготовлено для ${peopleFor(item.assignments.prepared)}. Пока этот тест не видит никто.`
        : 'Пока этот тест не видит никто. Проверьте вопросы и откройте его.';
    case 'public':
      return 'Этот тест видят все сотрудники и могут его пройти.';
    case 'assigned':
      return 'Этот тест видят только те, кому вы его дали.';
    default:
      return 'Тест убран в архив. Сотрудники его не видят, результаты сохранены.';
  }
}

const BREAKDOWN_ORDER: AssignmentState[] = ['available', 'prepared', 'paused', 'completed', 'expired'];

/** «Выдано: 2 · Подготовлено: 1 · …» — только ненулевые части; слова те же, что у выдач на вкладке «Кому выдан». */
export function assignmentBreakdown(item: Pick<OverviewItem, 'assignments'>): string {
  const counts = item.assignments;
  return BREAKDOWN_ORDER
    .filter((state) => counts[state] > 0)
    .map((state) => `${ASSIGNMENT_STATE_LABEL[state]}: ${counts[state]}`)
    .join(' · ');
}

export function foundText(item: Pick<OverviewItem, 'matched_people' | 'matched_people_total'>): string {
  const names = item.matched_people.map((person) => person.full_name).join(', ');
  const rest = item.matched_people_total - item.matched_people.length;
  return `Найдено по сотруднику: ${names}${rest > 0 ? ` и ещё ${rest}` : ''}`;
}

export const PASS_PERCENT = 70;

export const BLOCK_LABEL: Record<TestBlock, string> = {
  products: 'Ассортимент и запчасти',
  vehicles: 'Модели автомобилей',
};

/** Текст карточки «Состояние теста» на странице теста. */
export function stateCardText(item: StatusInput): string {
  switch (item.status) {
    case 'draft':
      return item.assignments.prepared > 0
        ? `Тест подготовлен для ${peopleFor(item.assignments.prepared)}: пока никто его не видит. Открыть доступ можно на вкладке «Кому выдан».`
        : 'Сейчас этот тест не видит никто. Когда будете готовы, откройте его: всем сотрудникам или только выбранным людям.';
    case 'public':
      return 'Сейчас этот тест видят все сотрудники и могут его пройти.';
    case 'assigned':
      return 'Сейчас этот тест видят только те, кому вы его дали.';
    default:
      return 'Тест в архиве: сотрудники его не видят, результаты сохранены.';
  }
}

export function stateCardHint(item: StatusInput): string {
  if (item.status === 'draft' && item.assignments.total === 0) {
    return 'Чтобы дать тест только выбранным людям, нажмите «Дать тест сотрудникам» выше.';
  }
  if (item.status === 'public') return 'Результаты тех, кто уже прошёл тест, сохранятся.';
  return '';
}

/** Первая строка вкладки «Кому выдан». */
export function givenIntro(item: StatusInput): string {
  switch (item.status) {
    case 'draft':
      return item.assignments.total > 0
        ? 'Выдача подготовлена, но пока не открыта: сотрудники этот тест не видят.'
        : 'Этот тест пока никому не выдан. Сейчас его не видит никто.';
    case 'public':
      return 'Этот тест пока никому не выдан. Сейчас его видят все сотрудники. Если дать тест конкретным людям, остальные перестанут его видеть.';
    case 'assigned':
      return 'Этот тест видят только эти сотрудники.';
    default:
      return 'Тест в архиве, его не видит никто.';
  }
}

/** Подпись о порядке вопросов: на странице теста и в мастере создания. */
export const ORDER_HINT =
  'Порядок нужен для вашего списка и для файлов выгрузки (Excel и HTML). '
  + 'В самой системе сотрудникам вопросы показываются вперемешку. Кнопки «Выше» и «Ниже» меняют ваш порядок.';

/** Варианты времени на тест: кнопки в мастере и в настройках теста. */
export const MINUTE_CHOICES = [10, 15, 20, 30, 45, 60];

const SKIP_REASON_TEXT: Record<SkippedQuestion['reason'], string> = {
  duplicate_in_test: 'уже были в тесте',
  duplicate_in_request: 'повторялись в выбранных',
  invalid_structure: 'в вопросе ошибка',
};

/** «Пропущено: 2 — уже были в тесте: 1, в вопросе ошибка: 1.»; пусто, если ничего не пропущено. */
export function skippedSummary(skipped: SkippedQuestion[]): string {
  if (skipped.length === 0) return '';
  const counts = new Map<SkippedQuestion['reason'], number>();
  skipped.forEach((entry) => counts.set(entry.reason, (counts.get(entry.reason) ?? 0) + 1));
  const parts = (Object.keys(SKIP_REASON_TEXT) as Array<SkippedQuestion['reason']>)
    .filter((reason) => counts.has(reason))
    .map((reason) => `${SKIP_REASON_TEXT[reason]}: ${counts.get(reason)}`);
  return `Пропущено: ${skipped.length} — ${parts.join(', ')}.`;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Дата с сервера. Backend пишет `datetime.utcnow().isoformat()`: время по UTC БЕЗ указания пояса («2026-09-01T22:30:00»),
 * а `new Date(...)` читает такую строку как местное время, и день в подписи около полуночи оказывался соседним.
 * Строка с поясом (`Z`, `+03:00`, `+0300`) и «только дата» разбираются как есть; время без пояса читается как UTC.
 * Доли секунды длиннее трёх цифр (микросекунды Python) обрезаются до миллисекунд: так строку разберёт любой движок.
 * Пусто, не задано или не разобрать — `null`.
 */
export function parseServerDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const source = DATE_ONLY.test(value) || HAS_ZONE.test(value)
    ? value
    : `${value.replace(/(\.\d{3})\d+$/, '$1')}Z`;
  const date = new Date(source);
  return Number.isNaN(date.getTime()) ? null : date;
}
